import { useEffect, useState } from 'react';

import type { TokenScope, TokenView, WebhookEvent, WebhookView } from '@noti/shared';

import type { WorkspaceSummary } from '../api';
import {
  archiveWebhook,
  createToken,
  createWebhook,
  fetchTokens,
  fetchWebhooks,
  revokeToken,
} from '../api';
import type { MessageKey } from '../i18n';
import { t } from '../i18n';

/** The five capabilities, in the words of the prototype's connection form. */
const CAPABILITIES: { scope: TokenScope; label: MessageKey }[] = [
  { scope: 'read', label: 'tokens.cap.read' },
  { scope: 'update', label: 'tokens.cap.update' },
  { scope: 'insert', label: 'tokens.cap.insert' },
  { scope: 'rcom', label: 'tokens.cap.rcom' },
  { scope: 'icom', label: 'tokens.cap.icom' },
];

const USER_INFO: { value: 'none' | 'noemail' | 'email'; label: MessageKey }[] = [
  { value: 'none', label: 'tokens.userInfo.none' },
  { value: 'noemail', label: 'tokens.userInfo.noemail' },
  { value: 'email', label: 'tokens.userInfo.email' },
];

/** The events the workspace actually writes; `*` catches anything new. */
const EVENTS: { group: MessageKey; events: { value: WebhookEvent; label: MessageKey }[] }[] = [
  {
    group: 'tokens.group.pages',
    events: [
      { value: 'page.created', label: 'tokens.event.pageCreated' },
      { value: 'page.updated', label: 'tokens.event.pageUpdated' },
      { value: 'page.saved', label: 'tokens.event.pageSaved' },
      { value: 'page.deleted', label: 'tokens.event.pageDeleted' },
    ],
  },
  {
    group: 'tokens.group.databases',
    events: [
      { value: 'data_source.updated', label: 'tokens.event.dataSourceUpdated' },
      { value: 'data_source.row_created', label: 'tokens.event.rowCreated' },
      { value: 'data_source.row_updated', label: 'tokens.event.rowUpdated' },
    ],
  },
  {
    group: 'tokens.group.comments',
    events: [{ value: 'comment.created', label: 'tokens.event.commentCreated' }],
  },
];

/** Zero days means the token has no expiry at all. */
const NO_EXPIRY = 0;

/** The line under a token's name: its state, its expiry and when it was last used. */
function describeToken(item: TokenView): string {
  if (item.revoked) return t('tokens.revoked');
  const lastUsed = item.lastUsedAt
    ? new Date(item.lastUsedAt).toLocaleDateString('en-GB')
    : t('tokens.never');
  if (item.expiresAt) {
    return `${t('tokens.expires')} ${new Date(item.expiresAt).toLocaleDateString('en-GB')} · ${t('tokens.lastUsed')} ${lastUsed}`;
  }
  // A connection never expires by design; a token can be created without expiry.
  if (item.kind === 'connection') return `${t('tokens.scopes')}: ${item.scopes.join(', ')}`;
  return `${t('tokens.expNever')} · ${t('tokens.lastUsed')} ${lastUsed}`;
}

export interface IntegrationTabProps {
  workspace: WorkspaceSummary;
  /** Base address clients should point at, e.g. https://noti.hopon.be. */
  baseUrl: string;
  onError: () => void;
}

/** Settings → Access tokens and Connections: one list, one form. */
export function TokensTab({
  workspace,
  baseUrl,
  onError,
  kind,
}: IntegrationTabProps & { kind: 'token' | 'connection' }): React.ReactElement {
  const [items, setItems] = useState<TokenView[]>([]);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<TokenScope[]>(['read']);
  const [userInfo, setUserInfo] = useState<'none' | 'noemail' | 'email'>('none');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [reveal, setReveal] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = (): void => {
    void fetchTokens(workspace.id, kind)
      .then(setItems)
      .catch(() => onError());
  };

  useEffect(reload, [workspace.id, kind]);

  const toggle = (scope: TokenScope): void => {
    setScopes((current) =>
      current.includes(scope) ? current.filter((entry) => entry !== scope) : [...current, scope],
    );
  };

  const create = (): void => {
    setError(null);
    void createToken(workspace.id, {
      name,
      kind,
      scopes,
      userInfo,
      ...(kind === 'token' && expiresInDays !== NO_EXPIRY ? { expiresInDays } : {}),
    })
      .then((created) => {
        setReveal(created.secret);
        setName('');
        reload();
      })
      .catch(() => {
        setError(t('error.generic'));
        onError();
      });
  };

  return (
    <div className="setdlg__scroll">
      {reveal ? (
        <div className="setdlg__reveal" role="status">
          <div className="setdlg__revealhead">{t('tokens.revealHead')}</div>
          <div className="setdlg__revealrow">
            <code>{reveal}</code>
            <button
              type="button"
              className="nb share__invitebtn"
              onClick={() => void navigator.clipboard.writeText(reveal)}
            >
              {t('share.copyLink')}
            </button>
            <button
              type="button"
              className="nb row setdlg__donerow"
              onClick={() => setReveal(null)}
            >
              {t('share.done')}
            </button>
          </div>
        </div>
      ) : null}

      <div className="setdlg__field">
        <label className="setdlg__label" htmlFor="token-name">
          {kind === 'token' ? t('tokens.name') : t('connections.name')}
        </label>
        <input
          id="token-name"
          className="ni setdlg__input"
          value={name}
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <div className="setdlg__caps">
        {CAPABILITIES.map((capability) => (
          <label className="setdlg__cap" key={capability.scope}>
            <input
              type="checkbox"
              checked={scopes.includes(capability.scope)}
              onChange={() => toggle(capability.scope)}
            />
            {t(capability.label)}
          </label>
        ))}
      </div>

      <div className="setdlg__field">
        <label className="setdlg__label" htmlFor="token-userinfo">
          {t('tokens.userInfoLabel')}
        </label>
        <select
          id="token-userinfo"
          className="ni setdlg__select"
          value={userInfo}
          onChange={(event) => setUserInfo(event.target.value as typeof userInfo)}
        >
          {USER_INFO.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.label)}
            </option>
          ))}
        </select>
      </div>

      {kind === 'token' ? (
        <div className="setdlg__field">
          <label className="setdlg__label" htmlFor="token-expires">
            {t('tokens.expiresAfter')}
          </label>
          <select
            id="token-expires"
            className="ni setdlg__select"
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(Number(event.target.value))}
          >
            <option value={1}>{t('tokens.exp1')}</option>
            <option value={7}>{t('tokens.exp7')}</option>
            <option value={30}>{t('tokens.exp30')}</option>
            <option value={NO_EXPIRY}>{t('tokens.expNever')}</option>
          </select>
        </div>
      ) : null}

      <button
        type="button"
        className="nb share__invitebtn setdlg__wide"
        onClick={create}
        disabled={name.trim().length === 0 || scopes.length === 0}
      >
        {kind === 'token' ? t('tokens.newToken') : t('connections.newConnection')}
      </button>

      {error ? (
        <p className="setdlg__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="setdlg__list">
        {items.length === 0 ? (
          <p className="setdlg__empty">{t('tokens.none')}</p>
        ) : (
          items.map((item) => (
            <div className="setdlg__listrow" key={item.id}>
              <div className="setdlg__rowtext">
                <div className="setdlg__rowlabel">{item.name}</div>
                <div className="setdlg__rowsub">
                  {item.prefix} · {describeToken(item)}
                </div>
              </div>
              <button
                type="button"
                className="nb setdlg__revoke"
                aria-label={`${t('tokens.revoke')} ${item.name}`}
                onClick={() => {
                  void revokeToken(workspace.id, item.id)
                    .then(reload)
                    .catch(() => onError());
                }}
              >
                {t('tokens.revoke')}
              </button>
            </div>
          ))
        )}
      </div>

      {kind === 'connection' ? (
        <p className="setdlg__note">{t('connections.hint', { url: `${baseUrl}/mcp` })}</p>
      ) : null}
    </div>
  );
}

/** Settings → Webhooks: subscribe, and see what was delivered. */
export function WebhooksTab({
  workspace,
  onError,
}: Pick<IntegrationTabProps, 'workspace' | 'onError'>): React.ReactElement {
  const [items, setItems] = useState<WebhookView[]>([]);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<WebhookEvent[]>(['page.created']);
  const [reveal, setReveal] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = (): void => {
    void fetchWebhooks(workspace.id)
      .then(setItems)
      .catch(() => onError());
  };

  useEffect(reload, [workspace.id]);

  const create = (): void => {
    setError(null);
    void createWebhook(workspace.id, { name, url, events })
      .then((created) => {
        setReveal(created.secret);
        setName('');
        setUrl('');
        reload();
      })
      .catch(() => {
        setError(t('webhooks.invalid'));
        onError();
      });
  };

  return (
    <div className="setdlg__scroll">
      {reveal ? (
        <div className="setdlg__reveal" role="status">
          <div className="setdlg__revealhead">{t('tokens.revealHead')}</div>
          <div className="setdlg__revealrow">
            <code>{reveal}</code>
            <button
              type="button"
              className="nb share__invitebtn"
              onClick={() => void navigator.clipboard.writeText(reveal)}
            >
              {t('share.copyLink')}
            </button>
            <button
              type="button"
              className="nb row setdlg__donerow"
              onClick={() => setReveal(null)}
            >
              {t('share.done')}
            </button>
          </div>
        </div>
      ) : null}

      <div className="setdlg__field">
        <label className="setdlg__label" htmlFor="hook-name">
          {t('webhooks.name')}
        </label>
        <input
          id="hook-name"
          className="ni setdlg__input"
          value={name}
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="setdlg__field">
        <label className="setdlg__label" htmlFor="hook-url">
          {t('webhooks.url')}
        </label>
        <input
          id="hook-url"
          className="ni setdlg__input"
          type="url"
          placeholder="https://example.com/hooks/noti"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
        />
      </div>

      {EVENTS.map((group) => (
        <fieldset className="setdlg__fieldset" key={group.group}>
          <legend className="setdlg__legend">{t(group.group)}</legend>
          {group.events.map((event) => (
            <label className="setdlg__cap" key={event.value}>
              <input
                type="checkbox"
                checked={events.includes(event.value)}
                onChange={() =>
                  setEvents((current) =>
                    current.includes(event.value)
                      ? current.filter((entry) => entry !== event.value)
                      : [...current, event.value],
                  )
                }
              />
              {t(event.label)}
            </label>
          ))}
        </fieldset>
      ))}

      <button
        type="button"
        className="nb share__invitebtn setdlg__wide"
        onClick={create}
        disabled={name.trim().length === 0 || url.trim().length === 0 || events.length === 0}
      >
        {t('webhooks.add')}
      </button>

      {error ? (
        <p className="setdlg__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="setdlg__list">
        {items.length === 0 ? (
          <p className="setdlg__empty">{t('webhooks.none')}</p>
        ) : (
          items.map((item) => (
            <div className="setdlg__listrow" key={item.id}>
              <div className="setdlg__rowtext">
                <div className="setdlg__rowlabel">{item.name}</div>
                <div className="setdlg__rowsub">
                  {item.url} · {item.events.join(', ')}
                </div>
              </div>
              <button
                type="button"
                className="nb setdlg__revoke"
                aria-label={`${t('webhooks.remove')} ${item.name}`}
                onClick={() => {
                  void archiveWebhook(workspace.id, item.id)
                    .then(reload)
                    .catch(() => onError());
                }}
              >
                {t('webhooks.remove')}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/** Settings → MCP: where to point a client, and how to do it. */
export function McpTab({ baseUrl }: { baseUrl: string }): React.ReactElement {
  const [client, setClient] = useState<'claude' | 'cursor' | 'stdio'>('claude');
  const url = `${baseUrl}/mcp`;
  const snippet =
    client === 'claude'
      ? `claude mcp add --transport http noti ${url}`
      : client === 'cursor'
        ? `{\n  "mcpServers": {\n    "noti": { "url": "${url}" }\n  }\n}`
        : `npx mcp-remote ${url}`;

  return (
    <div className="setdlg__scroll">
      <div className="setdlg__field">
        <span className="setdlg__label">{t('mcp.address')}</span>
        <div className="setdlg__address">
          <code>{url}</code>
          <button
            type="button"
            className="nb row setdlg__copy"
            aria-label={t('mcp.copyAddress')}
            onClick={() => void navigator.clipboard.writeText(url)}
          >
            copy
          </button>
        </div>
      </div>

      <div className="setdlg__tabs">
        {(
          [
            ['claude', 'mcp.client.claude'],
            ['cursor', 'mcp.client.cursor'],
            ['stdio', 'mcp.client.stdio'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={client === value ? 'share__tab nb is-on' : 'share__tab nb'}
            aria-pressed={client === value}
            onClick={() => setClient(value)}
          >
            {t(label)}
          </button>
        ))}
      </div>

      <pre className="setdlg__snippet">{snippet}</pre>

      <p className="setdlg__note">{t('mcp.bearerHint')}</p>
      <p className="setdlg__note">{t('mcp.toolsHint')}</p>
    </div>
  );
}
